<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faShoppingCart, faTrashAlt, faSave, faPrint, faShareAlt, faAngleLeft, faBoxOpen } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'
    import { QuotationEntity, QuotationPayment, ServiceOnQuotation } from '../../../modules/posApi/quotation/quotation.entity'
    import type { User } from '../../../modules/usersApi/users/user'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZsheetSection from '../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../_common/z/sheet/zsheet-entity.svelte'
    import ZsheetAdd from '../../_common/z/sheet/zsheet-add.svelte'
    import QuotationFormItems from './quotation-form-items.svelte'
    import QuotationFormPayments from './quotation-form-payments.svelte'
    import Popover from '../../_common/popover.svelte'
    import QuotationPrint from './quotation-print.svelte'
    import type { SharePageEntity } from '../../../modules/toolsApi/share-pages/share-page.entity'
    import SharePageLink from '../../_common/share-page-link.svelte'
    import { GridClass, GridCol, GridColTypeEnum } from '../../_common/z/zgrid'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import QuotationSheet from './v4/quotation-sheet.svelte'

    const dispatch = createEventDispatcher()

    export let quotationId: string = null

    let show = false
    let entity: QuotationEntity = new QuotationEntity()
    let createdAt = dayjs(new Date()).format('YYYY-MM-DD HH:mm')
    let newService: boolean = false
    let newPayment: boolean = false
    export let isModel: boolean = false
    let models: QuotationEntity[] = []

    let users: User[] = []

    onMount(async () => {
        users = await window.api.users.companies.getCompanyUsers(120)
        if (quotationId) {
            await loadEntity(quotationId)
        } else {
            newEntity()
        }
    })

    async function loadModels() {
        models = await window.api.pos.quotations.getAll({ isModel: true, service: true, payment: true })
        models = models.filter((el) => el.status >= 100)
    }

    let modelId = null
    async function onModelSelected() {
        await newEntity()
        if (!modelId) return
        const found = models.find((el) => el.quotationId === modelId)
        console.log('model-loaded', found)
        entity.expiresAt = found.expiresAt
        entity.notes = found.notes
        for (const item of found.items) {
            entity.items.push({
                parentServiceId: item.parentServiceId,
                quantity: item.quantity,
                service: item.service,
                serviceId: item.serviceId,
                type: item.type,
                value: item.value,
            })
        }
        entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items)
        for (const payment of found.payments) {
            entity.payments.push({
                dpMethod: payment.dpMethod,
                dpValue: payment.dpValue,
                installmentFrom: payment.installmentFrom,
                installmentMethod: payment.installmentMethod,
                installmentTo: payment.installmentTo,
                installmentValue: payment.installmentValue,
            })
        }
        entity = entity
    }

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new QuotationEntity()
            entity = await window.api.pos.quotations.getOne(id)
            createdAt = dayjs(entity.createdAt).format('YYYY-MM-DD HH:mm')
            if (entity.personId) {
                entity.person = await window.api.events.persons.getOne(entity.personId)
            }
            entity.user = users.find((el) => el.userId === entity.userId)
            entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items)
            console.log('quotation', entity)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function newEntity() {
        await loadModels()
        entity = new QuotationEntity()
        entity.createdAt = new Date(createdAt)
        entity.expiresAt = 10
        entity.isModel = isModel
        entity.items = []
        entity.payments = []
        entity.user = users.find((el) => el.userId === window.store.token.userId)
        entity.userId = entity.user?.userId
    }

    async function saveEntity() {
        upsertEntity()
    }

    async function upsertEntity() {
        try {
            if (!createdAt) {
                window.error('Informe data e hora de lançamento válidos')
                return false
            }
            if (show) return
            show = true
            // limpa itens nulos
            entity.items = entity.items.filter((el) => !!el)
            entity.payments = entity.payments.filter((el) => !!el)
            for (const pm of entity.payments) {
                if (!pm.dpValue) pm.dpValue = 0
                if (!pm.installmentValue) pm.installmentValue = 0
            }

            const dto: Partial<QuotationEntity> = {
                quotationId: entity.quotationId || undefined,
                createdAt: new Date(createdAt),
                notes: entity.notes,
                personId: entity.person?.personId,
                payments: entity.payments,
                expiresAt: entity.expiresAt,
                isModel: isModel,
                title: entity.title,
                items: entity.items
                    .filter((el) => !!el)
                    .map((el) => {
                        return {
                            quantity: el.quantity,
                            serviceId: el.service.serviceId,
                            value: el.value,
                            type: el.service.type,
                            parentServiceId: el.parentServiceId,
                        }
                    }),
                userId: entity.user?.userId,
            }

            if (!validateDto(dto)) return false
            const res = await window.api.pos.quotations.upsert(dto)
            dispatch('change', res)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto: Partial<QuotationEntity>) {
        if (!dto.isModel && !dto.personId) {
            window.error('Selecione o cliente')
            return false
        }
        if (dto.isModel && !dto.title) {
            window.error('Dê um nome para o modelo de orçamento')
            return false
        }
        if (dto.items.length === 0 || dto.payments.length === 0) {
            window.error('Você deve informar ao menos um serviço e uma opção de pagamento')
            return false
        }
        if (dto.items.length > 0 && dto.items.reduce((acc, el) => (acc += el.value), 0) < 0) {
            window.error('Informe um valor válido para todos os serviços')
            return false
        }
        return true
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.pos.quotations.remove(entity.quotationId)
            dispatch('change')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function newServiceItem() {
        newService = true
    }

    function addNestedItems(parent: ServiceOnQuotation, position: number) {
        if (!Array.isArray(parent.service?.services)) return
        let count = 0
        for (const nested of parent.service.services) {
            let invoiceItem: ServiceOnQuotation = new ServiceOnQuotation()
            invoiceItem.value = nested.price
            invoiceItem.quantity = nested.quantity
            invoiceItem.service = nested.service
            invoiceItem.serviceId = nested.serviceId
            invoiceItem.parentServiceId = parent.service.serviceId
            count++
            entity.items.splice(position + count, 0, invoiceItem)
        }
    }

    async function serviceAdded(event) {
        console.log('added', event.detail)

        entity.items.push(event.detail)
        if (event.detail.service.type === 'package') {
            addNestedItems(event.detail, entity.items.length - 1)
        }
        entity.items = entity.items
        newService = false
    }

    function serviceClosed() {
        newService = false
        // se removeu um pacote, remove os itens adicionados
        entity.items = entity.items.filter((el) => !!el)
        for (let index = entity.items.length - 1; index >= 0; index--) {
            if (!entity.items[index]?.parentServiceId) continue
            const found = entity.items.find((el) => el?.service?.serviceId === entity.items[index].parentServiceId)
            if (!found) {
                entity.items[index] = undefined
                continue
            }
        }
        entity.items = entity.items.filter((el) => !!el)
    }

    async function newPaymentMethod() {
        newPayment = true
    }

    async function paymentMethodAdded(event) {
        let pm: QuotationPayment = event.detail
        if (!pm.dpValue) pm.dpValue = 0
        if (!pm.installmentValue) pm.installmentValue = 0
        entity.payments.push(pm)
        entity.payments = entity.payments
        newPayment = false
    }

    function paymentMethodClose() {
        newPayment = false
    }

    let goInvoiceQuestion = false
    function goInvoice() {
        goInvoiceQuestion = true
    }

    function newInvoice() {
        goInvoiceQuestion = false
        dispatch('invoice', { quotation: entity, act: 'add' })
    }

    function editInvoice() {
        goInvoiceQuestion = false
        dispatch('invoice', { quotation: entity, act: 'edit' })
    }

    async function setOpen() {
        goInvoiceQuestion = false
        dispatch('invoice', { quotation: entity, act: 'open' })
    }

    async function setOpenAlert() {
        if (!confirm("Atenção: Cancelar a baixa não exclui o lançamento no PDV, apenas altera o status deste orçamento para 'em aberto'. Deseja continuar?"))
            return
        goInvoiceQuestion = false
        dispatch('invoice', { quotation: entity, act: 'open' })
    }

    async function setClosed() {
        goInvoiceQuestion = false
        dispatch('invoice', { quotation: entity, act: 'closed' })
    }

    function showEntities() {
        dispatch('cancel')
    }

    let print = false
    function goPrint() {
        window.printer({ component: [QuotationPrint], props: [{ quotation: entity }] })
    }

    // No mobile a mesma lógica deste arquivo é apresentada pela folha (./v4/quotation-sheet.svelte):
    // ela recebe o estado e chama as funções daqui — nenhuma regra é duplicada.
    // Mesmo critério da agenda (schedules-container) e do PDV: quem decide qual tela abrir é a
    // largura da tela do aparelho.
    const isMobile = screen.width <= 900

    // ---- apresentação (mesmas frases da folha do mobile, para as duas versões dizerem o mesmo) ----
    const money = (value: number) => window.api.utils.utils.toReal(value || 0)

    $: items = (entity?.items || []).filter((el) => !!el)
    $: payments = (entity?.payments || []).filter((el) => !!el)
    $: total = items.reduce((acc, el) => acc + (el.value || 0) * (el.quantity || 0), 0)

    $: modelOptions = [{ value: '', label: '[não utilizar modelo]' }, ...models.map((m) => ({ value: m.quotationId, label: m.title }))]
    const expiresOptions = Array.from({ length: 45 }, (_, i) => ({ value: i + 1, label: i + 1 === 1 ? '1 dia' : i + 1 + ' dias' }))

    // Saldo do cliente ao lado do nome dele, como na agenda e no PDV: quem monta um orçamento
    // costuma precisar saber que a pessoa já deve (ou tem crédito) antes de fechar o valor.
    let noPaid: number = 0
    async function loadInvoices(personId: string) {
        noPaid = 0
        if (!personId) return
        noPaid = await window.api.pos.invoices.getCreditAndDebitsFromPerson(personId)
    }
    $: loadInvoices(entity?.person?.personId)

    function itemLabel(item: Partial<ServiceOnQuotation>) {
        const type = item?.service?.type
        if (item?.parentServiceId) return ''
        if (type === 'package') return 'Pacote'
        if (type === 'product') return 'Produto'
        if (type === 'consumable') return 'Insumo'
        return 'Serviço'
    }

    function itemSubtitle(item: Partial<ServiceOnQuotation>) {
        if (item.service?.type === 'package') return ''
        return `${item.quantity || 0} × ${money(item.value)}`
    }

    let editingItem: number = null
    function openItem(index: number) {
        newService = false
        editingItem = index
    }
    function itemFinished() {
        editingItem = null
        serviceClosed()
    }

    // mesmas frases do editor (quotation-form-payments): "R$ 100,00 no cartão de crédito em até 6 vezes"
    function prep(metodo: string) {
        if (metodo === 'Dinheiro') return 'em'
        if (metodo === 'Pix') return 'por'
        if (metodo === 'Financeira') return 'via'
        if (metodo === 'Outros*') return 'via'
        return 'no'
    }

    function friendlyInstallment(payment: Partial<QuotationPayment>) {
        if (payment.installmentFrom === payment.installmentTo) {
            return payment.installmentFrom === 1 ? 'em 1 vez' : 'em ' + payment.installmentFrom + ' vezes'
        }
        if (payment.installmentFrom === 1) return 'em até ' + payment.installmentTo + ' vezes'
        return 'de ' + payment.installmentFrom + ' a ' + payment.installmentTo + ' vezes'
    }

    function paymentTitle(payment: Partial<QuotationPayment>) {
        const metodo = (payment.installmentMethod || '').toLowerCase()
        return `${money(payment.installmentValue)} ${prep(payment.installmentMethod)} ${metodo} ${friendlyInstallment(payment)}`
    }

    function paymentSubtitle(payment: Partial<QuotationPayment>) {
        if (!payment.dpValue) return 'Sem entrada'
        return `Entrada de ${money(payment.dpValue)} ${prep(payment.dpMethod)} ${(payment.dpMethod || '').toLowerCase()}`
    }

    let editingPayment: number = null
    function openPayment(index: number) {
        newPayment = false
        editingPayment = index
    }
    function paymentFinished() {
        editingPayment = null
        paymentMethodClose()
    }

    let shareToken: string = null
    async function goShare() {
        const dto: Partial<SharePageEntity> = {
            data: [{ type: 'quotation-print', data: { quotation: entity, shared: true } }],
        }
        const result = await window.api.tools.sharePages.create(dto)
        shareToken = result.sharePageId + '_' + result.token
    }
</script>

<LoadSpinner delay={0} {show}></LoadSpinner>
{#if shareToken}
    <Popover on:close={() => (shareToken = null)} width="780px" title="Link de compartilhamento">
        <SharePageLink token={shareToken} personId={entity.personId || entity.person?.personId}></SharePageLink>
    </Popover>
{/if}

{#if goInvoiceQuestion}
    <Popover title="O que você deseja fazer?" width="560px" height="160px" on:close={() => (goInvoiceQuestion = false)}>
        <div class="config-box m-3">
            <div class="go-invoice">
                {#if entity.invoiceId?.length >= 10}
                    <button class="btn btn-secondary" on:click={editInvoice}>Editar lançamento no PDV</button>
                    <button on:click={setOpenAlert} class="btn btn-secondary">Cancelar faturamento</button>
                {:else}
                    <button on:click={newInvoice} class="btn btn-secondary">Lançar no PDV</button>
                    {#if entity.invoiceId === null}
                        <button on:click={setClosed} class="btn btn-secondary">Registrar como faturado sem lançar no PDV</button>
                    {:else}
                        <button on:click={setOpen} class="btn btn-secondary">Cancelar faturamento</button>
                    {/if}
                {/if}
            </div>
        </div>
    </Popover>
{/if}
{#if isMobile}
    <QuotationSheet
        bind:entity
        bind:newService
        bind:newPayment
        bind:modelId
        {isModel}
        {show}
        {models}
        onSave={saveEntity}
        onRemove={removeEntity}
        onPrint={goPrint}
        onShare={goShare}
        onInvoice={goInvoice}
        {onModelSelected}
        onServiceAdded={serviceAdded}
        onServiceClosed={serviceClosed}
        onPaymentAdded={paymentMethodAdded}
        onPaymentClosed={paymentMethodClose}
        onBack={showEntities}
    />
{:else}
<div class="root m-bottom-sm">
    <Zact>
        {#if window.store.token.permissions.pos.sales.write}
            {#if isModel}
                <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
            {/if}
            <!-- Cliente e saldo na barra, ao lado do Salvar, como na agenda (appointment-form) e no
                 PDV. No modelo de orçamento não há cliente: lá o campo do bloco é o nome do modelo. -->
            {#if !isModel}
                <div class="act-person">
                    <ZentitySelect fonte="pessoa" label="Cliente" disabled={!!entity.quotationId} bind:value={entity.person} />
                </div>
                {#if noPaid > 0.1}
                    <div class="alert alert-warning mb-0 act-balance text-center">
                        Saldo em aberto de {noPaid.toFixed(2)}
                    </div>
                {/if}
                {#if noPaid < -0.1}
                    <div class="alert alert-success mb-0 act-balance text-center">
                        Saldo positivo de {(-noPaid).toFixed(2)}
                    </div>
                {/if}
            {/if}
            <!-- quem empurra os botões para a direita é o campo de cliente, que cresce. O espaçador
                 só é necessário no modelo, onde esse campo não existe. -->
            {#if isModel}
                <div class="spacer"></div>
            {/if}
            {#if window.store.token.permissions.pos.quotes?.write && !!entity.quotationId}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" on:click={removeEntity} />
            {/if}
            {#if window.store.token.permissions.pos.quotes?.write}
                {#if !isModel}
                    {#if entity.quotationId}
                        <Zbutton color="secondary" icon={faPrint} on:click={goPrint} />
                        <Zbutton color="secondary" icon={faShareAlt} on:click={goShare} />
                        <Zbutton color="secondary" icon={faShoppingCart} label={entity.invoiceId ? 'Faturamento' : 'Faturar'} on:click={goInvoice} />
                    {/if}
                    <Zbutton color="primary" icon={faSave} label="Salvar" disabled={show} on:click={() => saveEntity()} />
                {:else}
                    <Zbutton color="primary" icon={faSave} label="Salvar modelo" disabled={show} on:click={() => saveEntity()} />
                {/if}
            {/if}
        {/if}
    </Zact>
    <div class="sections">
        {#if isModel}
            <ZsheetSection label="Modelo">
                <Zinput type="text" label="Nome do modelo" bind:value={entity.title} />
            </ZsheetSection>
        {:else}
            <!-- o cliente subiu para a barra de ações, junto do Salvar -->
            {#if !entity.quotationId && models.length > 0}
                <ZsheetSection label="Partir de um modelo">
                    <Zinput type="select" label="Modelo" options={modelOptions} bind:value={modelId} on:change={onModelSelected} />
                </ZsheetSection>
            {/if}
        {/if}

        <ZsheetSection label="Validade">
            <Zinput type="select" label="Vale por" options={expiresOptions} bind:value={entity.expiresAt} />
        </ZsheetSection>

        <ZsheetSection label="Itens" meta={items.length ? money(total) : 'obrigatório'}>
            <div class="list">
                {#each entity.items || [] as item, index}
                    {#if item}
                        <div class:child={!!item.parentServiceId}>
                            {#if editingItem === index}
                                <div class="edit">
                                    <QuotationFormItems startEditing={true} bind:item={entity.items[index]} on:cancel={itemFinished} on:added={itemFinished} />
                                </div>
                            {:else}
                                <ZsheetEntity
                                    icon={item.service?.type === 'package' ? faBoxOpen : null}
                                    initials={item.parentServiceId || item.service?.type === 'package' ? '' : (item.service?.name || '?').substring(0, 1)}
                                    title={item.service?.name || '[item não cadastrado]'}
                                    subtitle={itemSubtitle(item)}
                                    clickable={true}
                                    on:click={() => openItem(index)}
                                >
                                    {#if itemLabel(item)}<span class="tag">{itemLabel(item)}</span>{/if}
                                    <svelte:fragment slot="trailing">
                                        {#if item.service?.type !== 'package'}
                                            <span class="amount">{money((item.value || 0) * (item.quantity || 0))}</span>
                                        {/if}
                                    </svelte:fragment>
                                </ZsheetEntity>
                            {/if}
                        </div>
                    {/if}
                {/each}

                {#if newService}
                    <div class="edit">
                        <QuotationFormItems on:cancel={serviceClosed} on:added={serviceAdded} />
                    </div>
                {:else}
                    <ZsheetAdd label="Item" on:click={newServiceItem} />
                {/if}
            </div>
        </ZsheetSection>

        <ZsheetSection label="Opções de pagamento" meta={payments.length ? `${payments.length} ${payments.length === 1 ? 'opção' : 'opções'}` : ''}>
            <div class="list">
                {#each entity.payments || [] as payment, index}
                    {#if payment}
                        {#if editingPayment === index}
                            <div class="edit">
                                <QuotationFormPayments
                                    startEditing={true}
                                    bind:item={entity.payments[index]}
                                    on:cancel={paymentFinished}
                                    on:added={paymentFinished}
                                />
                            </div>
                        {:else}
                            <ZsheetEntity
                                title={paymentTitle(payment)}
                                subtitle={paymentSubtitle(payment)}
                                clickable={true}
                                on:click={() => openPayment(index)}
                            />
                        {/if}
                    {/if}
                {/each}

                {#if newPayment}
                    <div class="edit">
                        <QuotationFormPayments on:cancel={paymentMethodClose} on:added={paymentMethodAdded} />
                    </div>
                {:else}
                    <ZsheetAdd label="Opção de pagamento" on:click={newPaymentMethod} />
                {/if}
            </div>
        </ZsheetSection>

        <ZsheetSection label="Observações">
            <div class="h-print">
                <Zinput type="textarea" label="Observações que aparecem no orçamento" rows={5} bind:value={entity.notes} />
            </div>
            <!-- no papel o campo não serve: sai o texto puro, que quebra em quantas linhas precisar -->
            <div class="notes-wrap h-screen">{entity.notes || ''}</div>
        </ZsheetSection>
    </div>
</div>
{/if}

<style>
    /* cliente e saldo na barra de ações, no mesmo arranjo da agenda e do PDV */
    .act-person {
        flex: 1 1 260px;
        min-width: 220px;
    }
    .act-balance {
        padding: 9px;
        flex: 0 1 auto;
    }
    .go-invoice {
        display: flex;
        align-items: stretch;
        flex-direction: column;
    }

    .go-invoice > * {
        margin-bottom: 16px;
    }

    .go-invoice .btn:last-child {
        margin-bottom: 0px;
    }

    .notes-wrap {
        white-space: pre-wrap;
    }

    /* pilha de blocos, a mesma da folha do mobile: cada assunto num cartão, separados por um vão */
    .sections {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin: 12px 8px;
    }
    .list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    /* o editor do item abre no lugar da linha, dentro do mesmo bloco */
    .edit {
        border: 1px solid var(--z-field-border);
        border-radius: var(--z-field-radius);
        padding: 8px;
    }
    /* item que pertence a um pacote entra recuado, preso ao pai */
    .child {
        padding-left: 16px;
    }
    .amount {
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
    }
    .tag {
        display: inline-block;
        margin-top: 2px;
        font-size: 11px;
        padding: 1px 6px;
        border-radius: 999px;
        background: var(--z-field-act-bg);
        opacity: 0.8;
    }

    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column-reverse;
        }
    }
</style>
